import React from 'react'
import { Tag } from 'antd'
import { getCachedUserAccessData } from '../../utils/appUtils'

export const MOVEMENT_STATUS_OPTIONS = ['Good', 'Hold', 'Damaged']

export const DSV_STATUS_OPTIONS = ['Pending', ...MOVEMENT_STATUS_OPTIONS]

export const MOVEMENT_STATUS_COLORS = {
  Good: 'green',
  Hold: 'gold',
  Damaged: 'red'
}

export const SOURCE_COLORS = {
  ANKO: 'blue',
  DSV: 'purple'
}

export const renderStatusTag = status => (
  status ? <Tag color={MOVEMENT_STATUS_COLORS[status]}>{status}</Tag> : '—'
)

// Backend limits (kol-store-backend-service inventoryMovement validation)
export const MAX_PRODUCTS_PER_MOVEMENT = 200
export const MAX_NOTES_LENGTH = 1000
export const MAX_NOTE_LENGTH = 500

// Short codes accepted in the CSV upload's fromStatus/toStatus columns, e.g. "G" for "Good".
export const MOVEMENT_STATUS_CODES = {
  G: 'Good',
  H: 'Hold',
  D: 'Damaged'
}

export const REASON_OPTIONS = [
  { id: 'R1', label: 'Kmart AU issued Remove from show' },
  { id: 'R2', label: 'Holding in DC awaiting certification' },
  { id: 'R3', label: 'Corp communication request while investigating' },
  { id: 'R4', label: 'On Hold awaiting a quality investigation.' },
  { id: 'R5', label: 'Remove & Hold notice distributed' }
]

// Formats a reason as "R1 - <name>"; falls back to the local label when the stored name is missing.
export const formatReason = (reasonCode, reasonName) => {
  if (!reasonCode) return ''
  const name = reasonName || REASON_OPTIONS.find(item => item.id === reasonCode)?.label
  return name ? `${reasonCode} - ${name}` : reasonCode
}

export const getInventoryMovementPermissions = () => {
  const permissions = getCachedUserAccessData()?.message?.permissions?.inventoryMovement || {}
  return {
    canRead: permissions.read === true,
    canWrite: permissions.write === true
  }
}

export const formatTimestamp = (date = new Date()) => (
  new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date).replace(',', '')
)

const parseStatus = (value = '') => (
  MOVEMENT_STATUS_OPTIONS.find(option => option.toLowerCase() === value.toLowerCase()) ||
  MOVEMENT_STATUS_CODES[value.toUpperCase()] ||
  null
)

const parseCsvLine = (line) => {
  const cells = []
  let cell = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index]
    if (character === '"' && quoted && line[index + 1] === '"') {
      cell += '"'
      index += 1
    } else if (character === '"') {
      quoted = !quoted
    } else if (character === ',' && !quoted) {
      cells.push(cell.trim())
      cell = ''
    } else {
      cell += character
    }
  }

  cells.push(cell.trim())
  return cells
}

// Parses a simple CSV (header: keycode,quantity,fromStatus,toStatus,reason,note) into product rows.
// Keycodes are only format-checked here; existence is validated against the products lookup API by the caller.
// The "fromStatus"/"toStatus" columns accept either a MOVEMENT_STATUS_CODES code (e.g. "G") or the full text.
// The "reason" column accepts either a REASON_OPTIONS id (e.g. "R1") or the full reason text.
export const parseProductsCsv = (csvText) => {
  const lines = csvText.split(/\r\n|\n/).map(line => line.trim()).filter(Boolean)
  if (lines.length === 0) {
    return { rows: [], errors: ['The CSV file is empty'] }
  }

  const header = parseCsvLine(lines[0]).map(cell => cell.toLowerCase())
  const keycodeIndex = header.indexOf('keycode')
  const quantityIndex = header.indexOf('quantity')
  const fromStatusIndex = header.indexOf('fromstatus')
  const toStatusIndex = header.indexOf('tostatus')
  const reasonIndex = header.findIndex(cell => cell === 'reason' || cell.startsWith('reason '))
  const noteIndex = header.indexOf('note')

  if (keycodeIndex === -1 || fromStatusIndex === -1 || toStatusIndex === -1) {
    return { rows: [], errors: ['CSV must contain "keycode", "fromStatus" and "toStatus" columns'] }
  }

  const rows = []
  const errors = []

  lines.slice(1).forEach((line, index) => {
    const cells = parseCsvLine(line)
    const keycode = cells[keycodeIndex] || ''
    const rowNumber = index + 2

    if (!/^\d+$/.test(keycode)) {
      errors.push(`Row ${rowNumber}: invalid keycode "${keycode}"`)
      return
    }

    const rawQuantity = quantityIndex !== -1 ? Number.parseInt(cells[quantityIndex], 10) : 1
    const quantity = Number.isFinite(rawQuantity) && rawQuantity > 0 ? rawQuantity : NaN
    if (Number.isNaN(quantity)) {
      errors.push(`Row ${rowNumber}: quantity must be a positive number`)
      return
    }

    const fromMovementStatus = parseStatus(cells[fromStatusIndex])
    const toMovementStatus = parseStatus(cells[toStatusIndex])
    if (!fromMovementStatus || !toMovementStatus) {
      errors.push(`Row ${rowNumber}: fromStatus and toStatus must be Good/Hold/Damaged (or G/H/D)`)
      return
    }
    if (fromMovementStatus === toMovementStatus) {
      errors.push(`Row ${rowNumber}: fromStatus and toStatus must differ`)
      return
    }

    const rawReason = reasonIndex !== -1 ? (cells[reasonIndex] || '') : ''
    const reasonOption = REASON_OPTIONS.find(
      option => option.id.toLowerCase() === rawReason.toLowerCase() || option.label.toLowerCase() === rawReason.toLowerCase()
    )
    const reasonCode = reasonOption ? reasonOption.id : ''

    rows.push({
      rowNumber,
      keycode,
      quantity,
      fromMovementStatus,
      toMovementStatus,
      reasonCode,
      note: noteIndex !== -1 ? (cells[noteIndex] || '').slice(0, MAX_NOTE_LENGTH) : ''
    })
  })

  return { rows, errors }
}
